<template>
  <div class="time-picker-custom">
    <label
      v-if="label"
      class="time-picker-custom__label">{{ label }}</label>

    <div class="time-picker-custom__fields">
      <template
        v-for="(field, index) in dataFields">
        <div
          :key="`${field.key}`"
          class="time-picker-custom__field"
          :class="{ 'is-active': dataActiveField === field.key }"
          @click="onFieldClick(field.key)">
          <font-awesome-icon
            :icon="dataClockIcon"
            class="time-picker-custom__icon" />
          <input
            ref="input"
            v-model="field.text"
            class="time-picker-custom__input"
            type="text"
            inputmode="numeric"
            maxlength="5"
            autocomplete="off"
            :placeholder="placeholder"
            @input="onFieldInput(field, $event)"
            @focus="onFieldFocus(field.key)"
            @blur="onFieldBlur(field.key)"
            @keydown.enter="onFieldEnter(field.key)"
            @keydown.esc="closeDropdown"
            @keydown.down.prevent="onArrow(1)"
            @keydown.up.prevent="onArrow(-1)">
        </div>

        <span
          v-if="index < dataFields.length - 1"
          :key="`separator-${field.key}`"
          class="time-picker-custom__separator">–</span>
      </template>
    </div>

    <div
      v-if="dataOpen"
      ref="dropdown"
      :class="[
        'time-picker-custom__dropdown',
        'time-picker-custom__dropdown--' + (dataActiveField === 'start' ? 'start' : 'end')
      ]">
      <div
        v-for="(option, index) in dataOptions"
        :key="option.time"
        class="time-picker-custom__option"
        :class="{
          'is-active': option.active || dataHoverIndex === index,
          'is-disabled': option.disabled
        }"
        role="option"
        @mousedown.prevent
        @mouseenter="dataHoverIndex = index"
        @mouseleave="dataHoverIndex = -1"
        @click="selectOption(option)">
        <span class="time-picker-custom__option-time">{{ option.time }}</span>
        <span
          v-if="!option.disabled"
          class="time-picker-custom__option-duration">{{ option.duration }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { faClock } from '@fortawesome/pro-regular-svg-icons';

const TIME_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

const pad = value => String(value).padStart(2, '0');

const formatTime = date => `${pad(date.getHours())}:${pad(date.getMinutes())}`;

export default {
  name: 'TimePickerCustom',

  props: {
    value: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'HH:mm'
    },
    step: {
      type: Number,
      default: 15
    }
  },

  data () {
    return {
      dataClockIcon: faClock,
      dataFields: [
        { key: 'start', text: '' },
        { key: 'end', text: '' }
      ],
      dataStart: null,
      dataEnd: null,
      dataActiveField: null,
      dataOpen: false,
      dataHoverIndex: -1,
      dataLastMousedown: 0
    };
  },

  computed: {
    dataOptions () {
      return this.dataActiveField === 'start'
        ? this.dataStartOptions
        : this.dataEndOptions;
    },

    // Все времена суток с шагом step (от 00:00 до 23:45)
    hoursOptions () {
      const base = this.dataStart || this.dataEnd || new Date();
      const options = [];
      const total = Math.floor((24 * 60) / this.step);

      for (let i = 0; i < total; i++) {
        const date = new Date(
          base.getFullYear(),
          base.getMonth(),
          base.getDate(),
          0,
          0,
          0,
          0
        );
        date.setMinutes(i * this.step);

        options.push({
          time: formatTime(date),
          date
        });
      }

      return options;
    },

    // Время начала: доступно любое время суток
    dataStartOptions () {
      return this.hoursOptions.map(option => ({
        time: option.time,
        date: option.date,
        duration: this.dataEnd
          ? this.formatDuration(this.minutesBetween(option.date, this.dataEnd))
          : '',
        active: this.isSameTime(option.date, this.dataStart)
      }));
    },

    // Время окончания: недоступно время раньше или равное началу периода
    dataEndOptions () {
      return this.hoursOptions.map(option => ({
        time: option.time,
        date: option.date,
        duration: this.dataStart
          ? this.formatDuration(this.minutesBetween(this.dataStart, option.date))
          : '',
        disabled: this.dataStart ? !(this.compareTime(option.date, this.dataStart) > 0) : false,
        active: this.isSameTime(option.date, this.dataEnd)
      }));
    }
  },

  watch: {
    value () {
      if (!this.isValueEqual()) {
        this.initFromValue();
      }
    }
  },

  mounted () {
    this.initFromValue();
    document.addEventListener('mousedown', this.handleDocumentMousedown);
  },

  beforeDestroy () {
    document.removeEventListener('mousedown', this.handleDocumentMousedown);
  },

  methods: {
    handleDocumentMousedown (event) {
      const inside = this.$el.contains(event.target);

      if (this.dataOpen && !inside) {
        this.closeDropdown();
      }

      // Запоминаем, что нажатие произошло внутри компонента (в т.ч. на скроллбаре)
      this.dataLastMousedown = inside ? Date.now() : 0;
    },

    initFromValue () {
      const [start, end] = this.value || [];

      this.dataStart = this.normalize(start);
      this.dataEnd = this.normalize(end);
      this.setFieldText('start', this.dataStart ? formatTime(this.dataStart) : '');
      this.setFieldText('end', this.dataEnd ? formatTime(this.dataEnd) : '');
    },

    normalize (value) {
      if (value instanceof Date && !isNaN(value)) {
        return this.toToday(value);
      }
      if (typeof value === 'string' && TIME_PATTERN.test(value)) {
        return this.parseTime(value);
      }
      if (typeof value === 'number' && !isNaN(value)) {
        return this.toToday(new Date(value));
      }
      return null;
    },

    // Переносим время (часы/минуты) указанной даты на сегодняшний день,
    // чтобы сравнения периода не зависели от дня в исходных данных
    toToday (date) {
      const today = new Date();

      return new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        date.getHours(),
        date.getMinutes(),
        0,
        0
      );
    },

    parseTime (text) {
      const match = TIME_PATTERN.exec(text.trim());

      if (!match) {
        return null;
      }

      const date = new Date();
      date.setHours(Number(match[1]), Number(match[2]), 0, 0);
      return date;
    },

    addMinutes (date, minutes) {
      return new Date(date.getTime() + minutes * 60 * 1000);
    },

    minutesBetween (from, to) {
      return Math.max(0, Math.round((to - from) / 60000));
    },

    // Время в минутах от начала дня (только часы/минуты)
    timeMinutes (date) {
      return date.getHours() * 60 + date.getMinutes();
    },

    // Сравнение двух дат только по времени суток
    compareTime (left, right) {
      return this.timeMinutes(left) - this.timeMinutes(right);
    },

    isSameTime (left, right) {
      return !!right &&
        left.getHours() === right.getHours() &&
        left.getMinutes() === right.getMinutes();
    },

    isValueEqual () {
      const [start, end] = this.value || [];

      return this.isSameTime(this.dataStart, this.normalize(start)) &&
        this.isSameTime(this.dataEnd, this.normalize(end));
    },

    formatDuration (minutes) {
      const hours = Math.floor(minutes / 60);
      const rest = minutes % 60;

      if (hours && rest) {
        return `${hours} h ${rest} min`;
      }
      if (hours) {
        return `${hours} h`;
      }
      return `${rest} min`;
    },

    getField (key) {
      return this.dataFields.find(field => field.key === key);
    },

    setFieldText (key, text) {
      this.getField(key).text = text;
    },

    focusField (key) {
      const field = this.getField(key);

      if (field) {
        const index = this.dataFields.indexOf(field);
        this.$refs.input && this.$refs.input[index] && this.$refs.input[index].focus();
      }
    },

    formatInput (raw) {
      const digits = raw.replace(/\D/g, '').slice(0, 4);

      if (digits.length > 2) {
        return `${digits.slice(0, 2)}:${digits.slice(2)}`;
      }
      return digits;
    },

    onFieldClick (key) {
      this.focusField(key);
    },

    onFieldFocus (key) {
      this.dataActiveField = key;
      this.dataOpen = true;
      this.scrollActiveIntoView();
    },

    onFieldBlur (key) {
      this.applyField(key);
      this.dataActiveField = null;

      // Закрываем dropdown, если фокус ушёл за пределы компонента (tab, клик вне).
      // Клик по скроллбару (mousedown внутри компонента) не должен закрывать список
      setTimeout(() => {
        const mousedownInside = Date.now() - this.dataLastMousedown < 200;

        if (this.dataOpen && !this.$el.contains(document.activeElement) && !mousedownInside) {
          this.closeDropdown();
        }
      }, 0);
    },

    onFieldInput (field, event) {
      field.text = this.formatInput(event.target.value);
    },

    onFieldEnter (key) {
      if (this.dataOpen && this.dataHoverIndex > -1) {
        const option = this.dataOptions[this.dataHoverIndex];

        if (option) {
          this.selectOption(option);
          return;
        }
      }

      this.applyField(key);
    },

    onArrow (delta) {
      if (this.dataActiveField !== 'start' && this.dataActiveField !== 'end') {
        return;
      }

      if (!this.dataOpen) {
        this.dataOpen = true;
        this.dataHoverIndex = this.getActiveOptionIndex();
        this.scrollActiveIntoView();
        return;
      }

      const options = this.dataOptions;
      const lastIndex = options.length - 1;

      if (lastIndex < 0) {
        return;
      }

      // Пропускаем недоступные (disabled) варианты
      let index = this.dataHoverIndex;

      if (index < 0 || (options[index] && options[index].disabled)) {
        index = this.getActiveOptionIndex();
      }

      for (let guard = 0; guard <= lastIndex; guard++) {
        index = (index + delta + options.length) % options.length;

        if (!options[index].disabled) {
          break;
        }
      }

      this.dataHoverIndex = index;
      this.scrollActiveIntoView();
    },

    getActiveOptionIndex () {
      const index = this.dataOptions.findIndex(option => option.active);

      return index > -1 ? index : 0;
    },

    scrollActiveIntoView () {
      this.$nextTick(() => {
        const dropdown = this.$refs.dropdown;

        if (!dropdown) {
          return;
        }

        const index = this.dataHoverIndex > -1
          ? this.dataHoverIndex
          : this.getActiveOptionIndex();

        const item = dropdown.children[index];

        if (item && item.scrollIntoView) {
          item.scrollIntoView({ block: 'nearest' });
        }
      });
    },

    applyField (key) {
      const field = this.getField(key);
      const date = this.parseTime(field.text);

      if (!date) {
        // Невалидные значения откатываем к последним сохранённым
        this.setFieldText(key, key === 'start'
          ? (this.dataStart ? formatTime(this.dataStart) : '')
          : (this.dataEnd ? formatTime(this.dataEnd) : ''));
        return false;
      }

      if (key === 'start') {
        this.dataStart = date;

        // Держим период в корректном порядке: end > start
        if (this.dataEnd && this.compareTime(date, this.dataEnd) >= 0) {
          this.dataEnd = this.addMinutes(date, this.step);
        }
      } else {
        this.dataEnd = date;

        if (this.dataStart && this.compareTime(date, this.dataStart) <= 0) {
          this.dataEnd = this.addMinutes(this.dataStart, this.step);
        }
      }

      this.setFieldText('start', this.dataStart ? formatTime(this.dataStart) : '');
      this.setFieldText('end', this.dataEnd ? formatTime(this.dataEnd) : '');
      this.emitValue();
      return true;
    },

    selectOption (option) {
      if (option.disabled) {
        return;
      }

      const key = this.dataActiveField === 'start' ? 'start' : 'end';

      if (key === 'start') {
        this.dataStart = option.date;

        // Держим период в корректном порядке: end > start
        if (this.dataEnd && this.compareTime(option.date, this.dataEnd) >= 0) {
          this.dataEnd = this.addMinutes(option.date, this.step);
        }
      } else {
        this.dataEnd = option.date;

        if (this.dataStart && this.compareTime(option.date, this.dataStart) <= 0) {
          this.dataEnd = this.addMinutes(this.dataStart, this.step);
        }
      }

      this.setFieldText('start', this.dataStart ? formatTime(this.dataStart) : '');
      this.setFieldText('end', this.dataEnd ? formatTime(this.dataEnd) : '');
      this.dataOpen = false;
      this.dataHoverIndex = -1;
      this.emitValue();
      this.focusField(key);
    },

    closeDropdown () {
      this.dataOpen = false;
      this.dataHoverIndex = -1;
    },

    emitValue () {
      this.$emit('input', [this.dataStart, this.dataEnd]);
    }
  }
};
</script>

<style lang="scss" scoped>
.time-picker-custom {
  position: relative;
  margin-bottom: 16px;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-size: 12px;
    color: rgba(34, 34, 34, 0.6);
  }

  &__fields {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    background-color: #eeeeef;
    border-radius: 8px;
  }

  &__field {
    flex: 1 1 0;
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    height: 34px;
    padding-left: 10px;
    padding-right: 10px;
    background-color: #ffffff;
    border: 1px solid transparent;
    border-radius: 6px;
    transition: border-color 0.2s ease, color 0.2s ease;

    &.is-active,
    &:focus-within {
      border-color: #20a052;
    }
  }

  // Vue 2 scoped не поддерживает вложенные селекторы `&.is-active &__icon`,
  // поэтому выносим правило на уровень родителя
  &__field.is-active &__icon,
  &__field:focus-within &__icon {
    color: #20a052;
    fill: #20a052 !important;
  }

  &__icon {
    flex-shrink: 0;
    width: 14px;
    height: 14px;
    color: rgba(13, 13, 13, 0.6);
    fill: rgba(13, 13, 13, 0.6) !important;
  }

  &__input {
    flex: 1 1 0;
    min-width: 0;
    width: 100%;
    padding: 0;
    background: transparent;
    border: none;
    outline: none;
    font-family: 'cera-regular';
    font-size: 14px;
    color: #222222;
  }

  &__separator {
    flex-shrink: 0;
    font-family: 'cera-regular';
    font-size: 16px;
    color: #222222;
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 8px;
    left: auto;

    &--start {
      right: auto;
      left: 8px;
    }
    width: calc((100% - 41px) / 2);
    max-height: 202px;
    overflow-y: auto;
    padding: 8px;
    padding-right: 0;
    background-color: #ffffff;
    border-radius: 6px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
    z-index: 10;

    // Тонкий скроллбар как в дизайне
    scrollbar-width: thin;
    scrollbar-color: rgba(13, 13, 13, 0.25) transparent;

    &::-webkit-scrollbar {
      width: 4px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: rgba(13, 13, 13, 0.25);
      background-clip: padding-box;
      border: 2px solid transparent;
      border-radius: 4px;

      &:hover {
        background-color: rgba(13, 13, 13, 0.4);
      }
    }
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    min-height: 30px;
    padding: 8px 4px;
    border-radius: 4px;
    cursor: pointer;

    &.is-active {
      background-color: rgba(32, 160, 82, 0.2);
    }

    &.is-active &-time {
      color: #20a052;
    }

    &.is-disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__option-time {
    font-family: 'cera-medium';
    font-size: 14px;
    color: #222222;
    white-space: nowrap;
  }

  &__option-duration {
    font-family: 'cera-regular';
    font-size: 12px;
    color: rgba(34, 34, 34, 0.6);
    white-space: nowrap;
  }
}
</style>
